import React, { useState } from 'react';
import { Shield, FileText, Calendar, AlertTriangle, Globe, Users, Handshake, TrendingUp, CheckSquare, Download, ChevronDown, ChevronUp, ListChecks, Home, CloudDrizzle, Flame, Trees } from 'lucide-react';

const phases = [
  {
    icon: <Shield className="w-6 h-6 text-primary" />, title: 'Resiliency (Before It Happens)',
    description: 'Proactive investment in people, processes, and partnerships.',
    checklist: [
      'Build partnerships with disaster-response nonprofits',
      'Map volunteering activities that boost resilience',
      'Support underrepresented communities year-round',
      'Use CORAA to identify high-risk regions & partners',
    ],
  },
  {
    icon: <FileText className="w-6 h-6 text-primary" />, title: 'Preparedness (Planning Phase)',
    description: 'Internal readiness + External community focus.',
    checklist: [
      'Establish a disaster response policy tied to CSR',
      'Upload crisis/business continuity plans to CORAA',
      'Align with UN observance days to educate employees',
      'Prepare flexible volunteering campaigns',
    ],
    templates: [
      { name: 'Emergency Communication Template', url: '#' },
      { name: 'Volunteer Campaign Brief', url: '#' },
    ],
  },
  {
    icon: <AlertTriangle className="w-6 h-6 text-primary" />, title: 'Response (When It Happens)',
    description: 'Act fast, act local, act with impact.',
    checklist: [
      'Identify vetted NPOs in affected areas via CORAA',
      'Launch real-time donation/volunteer campaigns',
      'Amplify credible info across channels',
      'Empower employee-led mutual aid efforts',
      'Validate supply needs before sending aid',
    ],
  },
  {
    icon: <Home className="w-6 h-6 text-primary" />, title: 'Recovery (After the Storm)',
    description: 'Long-term support that goes beyond relief.',
    checklist: [
      'Track active nonprofits in post-disaster zones',
      'Offer employee volunteering for rebuilding',
      'Use analytics to track recovery KPIs',
      'Integrate ESG metrics into sustainability reporting',
    ],
  },
];

const disasterPlaybooks = [
  {
    icon: <CloudDrizzle className="w-5 h-5 text-blue-500" />,
    name: 'Flash Flood',
    checklist: [
      'Monitor IMD/local weather alerts and communicate early warnings',
      'Prepare and distribute emergency supply/relief kits',
      'Partner with local NPOs and shelters for evacuation support',
      'Map high-risk areas and create evacuation routes',
      'Educate employees on flood safety and assembly points',
      'Coordinate post-flood clean-up and health drives',
    ],
    template: { name: 'Flash Flood Response Brief (India)', url: '#' },
  },
  {
    icon: <Flame className="w-5 h-5 text-orange-500" />,
    name: 'Forest Fire',
    checklist: [
      'Monitor fire advisories and air quality indexes (AQI)',
      'Support NPOs providing shelter and relief to displaced families',
      'Disseminate fire safety and evacuation education',
      'Provide PPE (masks, respirators) and first aid supplies',
      'Coordinate with local fire authorities for volunteer support',
      'Plan reforestation or habitat restoration volunteering post-fire',
    ],
    template: { name: 'Forest Fire Response Brief', url: '#' },
  },
  {
    icon: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
    name: 'Earthquake',
    checklist: [
      'Educate employees on “Drop, Cover, Hold On” drills',
      'Map safe assembly areas and evacuation routes',
      'Partner with NPOs for rapid response and relief distribution',
      'Prepare emergency supply kits and first aid',
      'Support temporary shelter and rebuilding for affected communities',
      'Promote mental health support for survivors',
    ],
    template: { name: 'Earthquake Response Brief (India)', url: '#' },
  },
];

export default function DisasterResponseGuide() {
  const [openPhase, setOpenPhase] = useState<number | null>(0);
  const [openPlaybook, setOpenPlaybook] = useState<number | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const toggleChecklist = (phaseIdx: number, idx: number) => {
    const key = `${phaseIdx}-${idx}`;
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Readiness calculations
  const totalPhaseItems = phases.reduce((sum, p) => sum + p.checklist.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const percent = totalPhaseItems > 0 ? Math.round((checkedCount / totalPhaseItems) * 100) : 0;
  let tier = '';
  if (percent < 33) tier = 'Novice Responder';
  else if (percent < 66) tier = 'Prepared Partner';
  else tier = 'Resilient Champion';
  let nextStep = '';
  for (let i = 0; i < phases.length; i++) {
    for (let j = 0; j < phases[i].checklist.length; j++) {
      const key = `${i}-${j}`;
      if (!checked[key]) {
        nextStep = phases[i].checklist[j];
        break;
      }
    }
    if (nextStep) break;
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <main className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row gap-8">
        {/* Main Content Column */}
        <div className="flex-1 min-w-0">
          {/* Hero Banner */}
          <section className="bg-gradient-to-r from-primary to-secondary text-white p-8 rounded-lg shadow-lg mb-8">
            <h2 className="text-4xl font-semibold">Disaster Response Readiness</h2>
            <p className="mt-2 text-lg">Assess your preparedness and get actionable tips to improve.</p>
          </section>
          {/* Table of Contents */}
          <nav className="mb-10 bg-white rounded-lg shadow p-4 sticky top-4 z-10">
            <span className="font-semibold text-gray-800 block mb-2">Quick Navigation</span>
            <ul className="flex flex-wrap gap-3 text-sm text-primary font-medium">
              <li><a href="#phases" className="hover:underline">Phases</a></li>
              <li><a href="#playbooks" className="hover:underline">Playbooks</a></li>
              <li><a href="#outcomes" className="hover:underline">Outcomes</a></li>
              <li><a href="#how-to-use" className="hover:underline">How to Use</a></li>
            </ul>
          </nav>
          {/* Guide Title */}
          <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
            <Shield className="w-8 h-8 text-primary" /> Disaster Response Guide for Corporates
          </h1>
          {/* Phases Section */}
          <section id="phases" className="space-y-6 mb-12">
            {phases.map((phase, phaseIdx) => (
              <div key={phase.title} className="bg-white rounded-lg shadow-md hover:shadow-xl transition p-6 mb-4">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 font-semibold text-lg focus:outline-none"
                  onClick={() => setOpenPhase(openPhase === phaseIdx ? null : phaseIdx)}
                  aria-expanded={openPhase === phaseIdx}
                >
                  <span className="flex items-center gap-2">{phase.icon} {phase.title}</span>
                  {openPhase === phaseIdx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {openPhase === phaseIdx && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-500 mb-2">{phase.description}</p>
                    <ul className="mb-2 space-y-2">
                      {phase.checklist.map((item, idx) => (
                        <li key={item} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!checked[`${phaseIdx}-${idx}`]}
                            onChange={() => toggleChecklist(phaseIdx, idx)}
                            className="accent-primary w-4 h-4"
                            id={`phase-${phaseIdx}-item-${idx}`}
                          />
                          <label htmlFor={`phase-${phaseIdx}-item-${idx}`} className="cursor-pointer select-none">
                            {item}
                          </label>
                        </li>
                      ))}
                    </ul>
                    {phase.templates && (
                      <div className="mt-2">
                        <div className="font-semibold text-sm mb-1 flex items-center gap-2"><FileText className="w-4 h-4" /> Templates</div>
                        <ul className="list-disc ml-6">
                          {phase.templates.map((tpl) => (
                            <li key={tpl.name}>
                              <a href={tpl.url} className="text-primary hover:underline flex items-center gap-1 text-sm"><Download className="w-4 h-4" /> {tpl.name}</a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </section>
          {/* Playbooks Section */}
          <section id="playbooks" className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><ListChecks className="w-6 h-6 text-primary" /> Disaster-Specific Playbooks</h2>
            <div className="space-y-4">
              {disasterPlaybooks.map((playbook, idx) => (
                <div key={playbook.name} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 font-semibold text-lg focus:outline-none"
                    onClick={() => setOpenPlaybook(openPlaybook === idx ? null : idx)}
                    aria-expanded={openPlaybook === idx}
                  >
                    <span className="flex items-center gap-2">{playbook.icon} {playbook.name}</span>
                    {openPlaybook === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {openPlaybook === idx && (
                    <div className="px-8 pb-4">
                      <ul className="mb-2 space-y-2">
                        {playbook.checklist.map((item, cidx) => (
                          <li key={item} className="flex items-center gap-2">
                            <CheckSquare className="w-4 h-4 text-gray-300" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* CTA: Need help designing a playbook? */}
          <div className="bg-white border border-primary/30 rounded-lg p-6 mt-8 flex flex-col md:flex-row items-center gap-4 shadow">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-6 h-6 text-primary" />
                <span className="font-semibold text-lg">Need help designing a Disaster Response playbook?</span>
              </div>
              <div className="text-gray-700 text-sm">Our team can help you craft a tailored, actionable plan for your organization.</div>
            </div>
            <button
              className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-2 rounded shadow transition"
              onClick={() => {
                const form = document.getElementById('contact-form-footer');
                if (form) form.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
        {/* Sticky Sidebar */}
        <aside className="w-full md:w-80 flex-shrink-0 md:sticky md:top-8 h-fit">
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="mb-4">
              <span className="block text-base font-semibold mb-1">Readiness Meter</span>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-2xl font-bold">{percent}%</span>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                  percent < 33 ? 'bg-red-200 text-red-800' :
                  percent < 66 ? 'bg-yellow-200 text-yellow-800' :
                  'bg-green-200 text-green-800'
                }`}>{tier}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div className="h-3 rounded-full bg-primary transition-all duration-300" style={{ width: `${percent}%` }} />
              </div>
              {nextStep && <p className="text-xs text-gray-600">Next: <span className="font-medium">{nextStep}</span></p>}
            </div>
            <div className="mt-6">
              <span className="block text-xs text-gray-400 mb-1">Progress is saved in your browser</span>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
